import _get from "lodash/get"

import { ENV } from "../constants/env"
import { mergeDataWithoutDuplicates } from "../utils/common/arrays"
import { baseRequest } from "./config.js"

const brandSlug = ENV.BRAND_SLUG
const isWindowDefined = typeof window !== "undefined"

export const eventAdapter = data => {
  const event = {
    ...data,
    ..._get(data, "attributes", {}),
    self: _get(data, "relationships.self.data", {}),
    owner: _get(data, "relationships.owner.data", {})
  }
  delete event.attributes
  delete event.relationships
  return event
}

const eventsAdapter = events => {
  const adaptedEventsData = events.map(item => {
    const event = { ...item.event, venue: item.venue, tickets: item.tickets }
    return event
  })
  return adaptedEventsData
}

/**
 * Fetch events from cache
 *
 * @returns {Promise<Object>} - Object containing cached events and latest start date
 */
const fetchCachedEvents = async () => {
  try {
    const response = await fetch("/cache/events.json")
    const jsonData = await response.json()
    const events = _get(jsonData, "events", [])

    return { cachedEvents: events }
  } catch (error) {
    console.warn("Could not fetch cached events", error)
    return { cachedEvents: [] }
  }
}

/**
 * Fetch events from API
 *
 * @param {string} slug - Event slug
 * @param {Object} params - API parameters
 * @returns {Promise<Array>} - Array of events
 */
const fetchLiveEvents = async (slug, params) => {
  const response = await baseRequest.get(`events/${slug}?format=json&client=open-ground`, { params })

  return _get(response, "data.events", [])
}

/**
 * Get events.
 * - If `options.fromCache` is true → loads from cache and combines with fresh data
 * - Otherwise → fetches directly from API
 *
 * @param {Object} params - Query parameters
 * @param {Object} options - Options
 * @param {boolean} options.fromCache - Whether to use cache
 * @returns {Promise<Array>} - Array of events
 *
 * @see ./gatsby-node.js (writes cache to /static/cache/events.json)
 * @see https://github.com/theticketfairy/tf-client-checkout-open-ground/blob/main/gatsby-node.js
 */
export const getEvents = async (params = {}, options = { fromCache: false }) => {
  // Prepare API parameters
  const { slug, ...searchParams } = {
    ...{
      compact: "event-list",
      sectionType: "upcoming",
      slug: brandSlug
    },
    ...params
  }

  try {
    // Handle cached + live data approach
    if (options.fromCache) {
      // Step 1: Get cached data and find latest date
      const { cachedEvents } = await fetchCachedEvents()
      let latestDate = null
      let latestDateString = null

      cachedEvents.forEach(item => {
        if (item.event?.start_date) {
          const eventDate = new Date(item.event.start_date)
          if (!latestDate || eventDate > latestDate) {
            latestDate = eventDate
            latestDateString = item.event.start_date
          }
        }
      })

      // Step 2: Add latest date to search parameters
      const updatedParams = { ...searchParams }
      if (latestDateString) {
        updatedParams.date = latestDateString
      }

      // Step 3: Fetch fresh data with date parameter
      const liveEvents = await fetchLiveEvents(slug, updatedParams)

      // Step 4: Merge cached and live data without duplicates
      const mergedEvents = mergeDataWithoutDuplicates({
        oldData: cachedEvents,
        newData: liveEvents,
        identifier: "event.id"
      })

      return eventsAdapter(mergedEvents)
    }

    // Simple API-only approach
    const events = await fetchLiveEvents(slug, searchParams)
    return eventsAdapter(events)
  } catch (error) {
    console.error("getEvents error", error)
    throw error
  }
}

export const getEventsV2 = async () => {
  try {
    const res = await baseRequest.get(`api/v1/events/current`)
    const data = _get(res, "data", [])
    return data
  } catch (e) {
    console.log("getEvents error", e)
    throw e
  }
}

export const getEvent = async (slug, previewKey) => {
  try {
    const headers = {}
    if (isWindowDefined) {
      headers["authorization-guest"] = window.localStorage.getItem("auth_guest_token")
    }

    const res = await baseRequest.get(`/event/${slug}/?format=json&client=open-ground-test`, {
      params: {
        pk: previewKey
      },
      headers
    })
    const data = _get(res, "data")
    return data
  } catch (e) {
    console.log("getEvent error", e)
    throw e
  }
}

export const getProtectedEvent = async (password, id) => {
  try {
    const res = await baseRequest.post(`api/v1/event/${id}/authenticate`, {
      attributes: {
        data: {
          password
        }
      }
    })
    const data = _get(res, "data")
    const { headers } = res
    const session = headers["authorization-guest"]
    if (isWindowDefined && session) {
      window.localStorage.setItem("auth_guest_token", session)
    }
    return data
  } catch (e) {
    console.log("getProtectedEvent error", e)
    throw e
  }
}

export const getEventSliderData = async (slug = brandSlug) => {
  try {
    const res = await baseRequest.get(`events/${slug}?format=json&client=open-ground-test&isSlider=true`)
    const data = _get(res, "data.events", [])
    return data
  } catch (e) {
    console.log("getEventSliderData error", e)
    throw e
  }
}
